/**
 * Which results the Compare surfaces dialog can offer for the picked inputs
 * (spec §3 availability matrix, §12.1 compare mode), and which form side each
 * role sits on.
 *
 * Roles never change the engines: role A is always the cut & fill `top` and
 * the deviation reference; role B is always `bottom` and the deviation
 * source. Only the role NAMES follow the compare mode.
 */
import { isIfcModelLayer, isPointcloudLayer, type LayerLike } from '@/lib/analyses/candidates';

export const COMPARE_RESULTS = ['cutFill', 'deviation'] as const;
export type CompareResult = (typeof COMPARE_RESULTS)[number];

export const COMPARE_KINDS = ['design', 'survey'] as const;
export type CompareKind = (typeof COMPARE_KINDS)[number];

export const DEVIATION_MODES = ['vertical', 'normal', 'nearest', 'horizontal'] as const;
export type DeviationMode = (typeof DEVIATION_MODES)[number];

const IFC_MODES: readonly DeviationMode[] = ['nearest', 'horizontal'];
const POINTCLOUD_MODES: readonly DeviationMode[] = ['vertical'];

export const REASON_DEVIATION_NEEDS_DESIGN = 'compareSurfaces.reason.deviationNeedsDesign';
export const REASON_NEEDS_POINTCLOUD_AS_BUILT = 'compareSurfaces.reason.needsPointcloudAsBuilt';

export interface ResultAvailability {
  /** The card's PostHog flag is on. A hidden card is never offered. */
  visible: boolean;
  /** The picked inputs allow this result. */
  available: boolean;
  /** Why not, as an i18n key — shown in muted text inside the card. */
  reasonKey: string | null;
}

/**
 * The deviation modes a design layer supports.
 *
 * - IFC / FRAGMENT → nearest + horizontal: solids give no XY-containment
 *   lookup (`modeErrorForReference` refuses the rest server-side).
 * - Point cloud → vertical only: the reference is rasterised to a DSM.
 * - Everything else → all four. That includes a DXF design (Task 8 Step 3):
 *   `resolveModelReference` accepts `.dxf`, `deviation.py` reads its 3DFACE
 *   entities into a TIN with an XY trifinder (vertical/normal work), and the
 *   worker image ships `ezdxf`. A DXF upload usually lands as a VECTOR
 *   (GeoJSON) layer, which was already allowed every mode.
 * - No design picked yet → all four, so nothing is ruled out early.
 */
export function allowedDeviationModes(design?: LayerLike): readonly DeviationMode[] {
  if (!design) return DEVIATION_MODES;
  if (isPointcloudLayer(design)) return POINTCLOUD_MODES;
  if (isIfcModelLayer(design)) return IFC_MODES;
  return DEVIATION_MODES;
}

/** Why a design narrows the deviation modes, as i18n keys. */
export const REASON_MODES_IFC = 'compareSurfaces.modes.unavailableIfc';
export const REASON_MODES_POINTCLOUD = 'compareSurfaces.settings.pcVerticalOnly';

/**
 * `allowedDeviationModes` plus WHY the design narrows them (an i18n key, or
 * null when every mode is allowed) — the one place the dialog reads the
 * reason from, so the text cannot drift from the rule.
 */
export function deviationModeRestriction(design?: LayerLike): {
  modes: readonly DeviationMode[];
  reasonKey: string | null;
} {
  const modes = allowedDeviationModes(design);
  if (modes.length === DEVIATION_MODES.length) return { modes, reasonKey: null };
  return { modes, reasonKey: design && isPointcloudLayer(design) ? REASON_MODES_POINTCLOUD : REASON_MODES_IFC };
}

/**
 * The two result cards' state.
 *
 * Cut & fill works for any A + B. The deviation map needs Design mode (its
 * raw sign would read added ground as cut, §12.1) and a point cloud as B (it
 * colours the as-built's points). An input that is not picked yet rules
 * nothing out.
 */
export function resultAvailability(args: {
  kind: CompareKind;
  a?: LayerLike;
  b?: LayerLike;
  flags: { cutFill: boolean; deviation: boolean };
}): Record<CompareResult, ResultAvailability> {
  const { kind, b, flags } = args;
  const deviationReason =
    kind === 'survey'
      ? REASON_DEVIATION_NEEDS_DESIGN
      : b && !isPointcloudLayer(b)
        ? REASON_NEEDS_POINTCLOUD_AS_BUILT
        : null;
  return {
    cutFill: { visible: flags.cutFill, available: true, reasonKey: null },
    deviation: { visible: flags.deviation, available: deviationReason === null, reasonKey: deviationReason },
  };
}

export type CompareRole = 'design' | 'asBuilt' | 'laterSurvey' | 'earlierSurvey';

export interface RoleSlot {
  /** The `SurfacePicker` prefix (`${prefix}LayerId` …) under `inputs`. */
  prefix: 'top' | 'bottom';
  role: CompareRole;
  labelKey: string;
}

/**
 * Roles A and B, in display order. A is always the cut & fill `top`, so in
 * Earlier-survey mode the LATER survey is `top` and Fill reads as ground
 * added (spec §12.1) — no engine change. The words "top" and "bottom" are
 * form plumbing only and never reach the screen.
 */
export function roleSlots(kind: CompareKind): readonly [RoleSlot, RoleSlot] {
  const [a, b]: [CompareRole, CompareRole] = kind === 'survey' ? ['laterSurvey', 'earlierSurvey'] : ['design', 'asBuilt'];
  return [
    { prefix: 'top', role: a, labelKey: `compareSurfaces.roles.${a}` },
    { prefix: 'bottom', role: b, labelKey: `compareSurfaces.roles.${b}` },
  ];
}

/** The result selection plus which results the dialog unticked by itself. */
export interface ResultsState {
  results: CompareResult[];
  /** Unticked because they became unavailable — the only ones that may be
   *  ticked back automatically. */
  autoUnticked: CompareResult[];
}

export interface ResultsNote {
  change: 'unticked' | 'reticked';
  result: CompareResult;
  /** For `unticked`: why the result became unavailable. */
  reasonKey: string | null;
  /** For `unticked`: the result ticked in its place, so the run keeps one
   *  (`reconcileWithFallback`). */
  substitute?: CompareResult;
}

/**
 * Keeps the ticked results consistent with what the inputs allow (spec §4.2,
 * §12.8). Called after every change with the previous state, the selection
 * as it stands now (the user may just have changed it) and the current
 * availability:
 *   - a ticked result that is unavailable (or hidden) is unticked and
 *     remembered as auto-unticked, with a note naming the reason;
 *   - an auto-unticked result is ticked again once it is available, with a
 *     note; a result the USER unticked never is.
 * Ticking an auto-unticked result by hand clears its "auto" mark.
 */
export function reconcileResults(
  prev: ResultsState,
  next: readonly CompareResult[],
  availability: Record<CompareResult, ResultAvailability>,
): ResultsState & { notes: ResultsNote[] } {
  const usable = (r: CompareResult) => availability[r].visible && availability[r].available;
  const notes: ResultsNote[] = [];
  let auto = prev.autoUnticked.filter((r) => !next.includes(r));
  const results: CompareResult[] = [];
  for (const r of COMPARE_RESULTS) {
    if (next.includes(r)) {
      if (usable(r)) results.push(r);
      else {
        auto = [...auto.filter((x) => x !== r), r];
        notes.push({ change: 'unticked', result: r, reasonKey: availability[r].reasonKey });
      }
    } else if (auto.includes(r) && usable(r)) {
      results.push(r);
      auto = auto.filter((x) => x !== r);
      notes.push({ change: 'reticked', result: r, reasonKey: null });
    }
  }
  return { results, autoUnticked: auto, notes };
}

/** `ResultsState` plus the results the dialog ticked IN PLACE of an
 *  auto-unticked one. */
export interface FallbackResultsState extends ResultsState {
  autoTicked: CompareResult[];
}

/**
 * `reconcileResults`, plus a stand-in so an automatic untick never leaves the
 * run with nothing selected (spec §12.8 polish):
 *   - when unticking empties the selection, the first usable result is ticked
 *     in its place and the note names it (`substitute`);
 *   - when the unticked result comes back, the stand-in is unticked again, so
 *     the user is back on exactly what they picked (never two heavy jobs the
 *     user did not ask for, §4.1);
 *   - a stand-in the user unticks themselves is no longer a stand-in.
 */
export function reconcileWithFallback(
  prev: FallbackResultsState,
  next: readonly CompareResult[],
  availability: Record<CompareResult, ResultAvailability>,
): FallbackResultsState & { notes: ResultsNote[] } {
  const r = reconcileResults(prev, next, availability);
  let results = r.results;
  let notes = r.notes;
  let autoTicked = prev.autoTicked.filter((x) => next.includes(x));

  if (autoTicked.length > 0 && notes.some((n) => n.change === 'reticked')) {
    results = results.filter((x) => !autoTicked.includes(x));
    autoTicked = [];
  }

  if (results.length === 0 && notes.some((n) => n.change === 'unticked')) {
    const substitute = COMPARE_RESULTS.find(
      (x) => availability[x].visible && availability[x].available && !r.autoUnticked.includes(x),
    );
    if (substitute) {
      results = [substitute];
      autoTicked = [substitute];
      notes = notes.map((n) => (n.change === 'unticked' ? { ...n, substitute } : n));
    }
  }
  return { results, autoUnticked: r.autoUnticked, autoTicked, notes };
}
